import { templatesQuery, useDeleteTemplateMutation, type EmailTemplate } from "@/api/campaigns";
import { DataTable } from "@/components/data-table/DataTable";
import { DataTableBody } from "@/components/data-table/DataTableBody";
import { DataTableHeader } from "@/components/data-table/DataTableHeader";
import { StyledLink } from "@/components/link/StyledLink";
import { Button } from "@/components/ui/Button";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { DropdownMenu, DropdownMenuItem } from "@/components/ui/DropdownMenu";
import { IconButton } from "@/components/ui/IconButton";
import { seo } from "@/utils/seo";
import { toast } from "@givenwell/components";
import { DotsIcon } from "@givenwell/core-solid-icons";
import { colors } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute("/campaigns/templates/")({
  component: TemplatesPage,
  head: () => ({ meta: seo({ title: "Email Templates" }) }),
});

function TemplatesPage() {
  const query = useQuery(templatesQuery());
  const navigate = useNavigate();
  const deleteMut = useDeleteTemplateMutation();

  const rows = query.data || [];

  return (
    <PageWrapper
      title="Email Templates"
      rightContent={
        <Button
          variant="tonal"
          color="primary"
          onClick={() => navigate({ to: "/campaigns/templates/$templateId", params: { templateId: "new" } })}
        >
          New Template
        </Button>
      }
    >
      <DataTable>
        <DataTableHeader hideToggle />
        <DataTableBody
          data={rows}
          status={query.status}
          dataKey="email_template_id"
          columns={[
            {
              header: "Name",
              cell: (template: EmailTemplate) => (
                <StyledLink to="/campaigns/templates/$templateId" params={{ templateId: template.email_template_id }}>
                  {template.name}
                </StyledLink>
              ),
            },
            {
              header: "Subject",
              cell: (template: EmailTemplate) => template.subject,
            },
            {
              header: "Created by",
              cell: (template: EmailTemplate) => template.created_by_name ?? "-",
            },
            {
              header: "Updated by",
              cell: (template: EmailTemplate) => template.updated_by_name ?? "-",
            },
            {
              header: "Updated",
              cell: (template: EmailTemplate) => new Date(template.updated_at).toLocaleDateString(),
            },
            {
              header: "",
              css: { align: "right" },
              cell: (template: EmailTemplate) => (
                <DropdownMenu
                  align="end"
                  content={
                    <DropdownMenuItem
                      color="red"
                      onSelect={() => {
                        deleteMut.mutate(template.email_template_id, {
                          onSuccess: () => toast.success("Template deleted"),
                        });
                      }}
                    >
                      Delete
                    </DropdownMenuItem>
                  }
                >
                  <IconButton aria-label="Actions" variant="plain" size="sm" color="neutral">
                    <DotsIcon style={{ color: colors.text2 }} />
                  </IconButton>
                </DropdownMenu>
              ),
            },
          ]}
        />
      </DataTable>
    </PageWrapper>
  );
}
